import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAuth } from '../../../utils/auth';
import api, { adminAPI } from '../../../services/api';
import InventoryStylePagination from '../../../components/InventoryStylePagination';
import { FiPlus, FiSearch, FiFilter, FiRefreshCw } from 'react-icons/fi';
import { LuEye, LuSquarePen } from 'react-icons/lu';
import PermissionButton from '../../../components/PermissionButton';
import { PermissionGate } from '../../../contexts/PermissionContext';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole } from '../../../utils/roles';

const CustomersList = () => {
  // Onboarding configures billing, so creation is billing-role gated
  // on the backend (RF15) -- hide the entry point for other roles.
  const { user } = useAuth();
  const canCreateCustomer = hasBillingRole(user);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    hasActiveRequests: ''
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1
  });

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      // Verify authentication before making request
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        console.error('CustomersList: No authentication token available - aborting fetch');
        setError('Authentication required. Please log in again.');
        window.location.href = '/';
        return;
      }
      // Add request parameters including search and filters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      };
      
      const response = await adminAPI.getCustomers(params);
      
      // Check if we got a valid response
      if (response && Array.isArray(response.customers)) {
        setCustomers(response.customers);
        setPagination(response.pagination || {
          total: response.customers.length,
          page: 1,
          limit: 25,
          pages: 1
        });
      } else {
        console.error('CustomersList: Invalid response format', response);
        setError('Invalid data format received from server.');
      }
    } catch (err) {
      console.error('CustomersList: Error fetching customers:', err);
      setError('Failed to load customers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [pagination.page, searchTerm, filters]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle filter changes
  const handleStatusFilterChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleActiveRequestsFilterChange = (e) => {
    setFilters({
      ...filters,
      hasActiveRequests: e.target.value
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      hasActiveRequests: ''
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleToggleStatus = async (customerId) => {
    try {
      // Instead of making an API call, let's just toggle the status locally
      setCustomers(customers.map(customer => 
        customer.id === customerId 
          ? { ...customer, active: !customer.active, status: !customer.active ? 'active' : 'inactive' } 
          : customer
      ));
    } catch (err) {
      console.error('Error toggling customer status:', err);
      setError('Failed to update customer status.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Customers</h1>
          <p className="mt-2 text-sm text-gray-700">Manage all customer accounts and associated users.</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          {canCreateCustomer && (
          <PermissionButton
            permissions="create_customers"
            onClick={() => window.location.href = '/admin/customers/new'}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <FiPlus className="mr-2" /> Add Customer
          </PermissionButton>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="mt-6 mb-8">
        <div className="mb-4">
          <form onSubmit={handleSearch} className="relative rounded-md shadow-sm max-w-md">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by company name, email, or contact..."
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </form>
        </div>

        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500 hidden sm:block" />
            <select
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-center">
            <select
              value={filters.hasActiveRequests}
              onChange={handleActiveRequestsFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Customers</option>
              <option value="true">Has Active Requests</option>
              <option value="false">No Active Requests</option>
            </select>
          </div>
        </div>
      <div className="flex items-center justify-between mt-2">
        <div className="text-sm text-gray-500">
          {pagination.total > 0 && (
            <span>Showing {customers.length} of {pagination.total} customers</span>
          )}
        </div>
      </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Company Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Primary Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Requests
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.length > 0 ? (
                customers.map((customer) => (
                  <tr key={customer.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium"><Link to={`/admin/customers/${customer.id}`} className="text-indigo-600 hover:text-indigo-900">{customer.companyName || customer.name}</Link></div>
                      <div className="text-xs text-gray-500">{customer.contactName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{customer.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {customer.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {customer.activeRequests || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-3">
                        <PermissionGate permissions="view_customers">
                          <Link
                            to={`/admin/customers/${customer.id}`}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <LuEye className="h-5 w-5" />
                            <span className="sr-only">View</span>
                          </Link>
                        </PermissionGate>
                        
                        <PermissionButton
                          permissions="edit_customers"
                          onClick={() => window.location.href = `/admin/customers/${customer.id}/edit`}
                          className="text-blue-600 hover:text-blue-900 p-0 border-0 bg-transparent shadow-none focus:ring-0"
                          hideIfDenied={false}
                        >
                          <LuSquarePen className="h-5 w-5" />
                          <span className="sr-only">Edit</span>
                        </PermissionButton>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm || filters.status || filters.hasActiveRequests 
                      ? 'No customers match your search criteria'
                      : 'No customers found'
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden">
          {customers.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {customers.map((customer) => (
                <div key={customer.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div>
                        <div className="text-sm font-medium"><Link to={`/admin/customers/${customer.id}`} className="text-indigo-600 hover:text-indigo-900">{customer.companyName || customer.name}</Link></div>
                        <div className="text-xs text-gray-500">{customer.contactName}</div>
                      </div>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        customer.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {customer.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <PermissionGate permissions="view_customers">
                        <Link
                          to={`/admin/customers/${customer.id}`}
                          className="text-indigo-600 hover:text-indigo-900 p-1"
                        >
                          <LuEye className="h-5 w-5" />
                        </Link>
                      </PermissionGate>
                      
                      <PermissionButton
                        permissions="edit_customers"
                        onClick={() => window.location.href = `/admin/customers/${customer.id}/edit`}
                        className="text-blue-600 hover:text-blue-900 p-1 border-0 bg-transparent shadow-none focus:ring-0"
                        hideIfDenied={false}
                      >
                        <LuSquarePen className="h-5 w-5" />
                      </PermissionButton>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Email:</span>
                      <span className="text-gray-900">{customer.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Active Requests:</span>
                      <span className="text-gray-900">{customer.activeRequests || 0}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-sm text-gray-500">
              {searchTerm || filters.status || filters.hasActiveRequests 
                ? 'No customers match your search criteria'
                : 'No customers found'
              }
            </div>
          )}
        </div>

        <InventoryStylePagination
          currentPage={pagination.page}
          totalPages={pagination.pages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={(nextLimit) => setPagination((prev) => ({ ...prev, limit: nextLimit, page: 1 }))}
        />
      </div>
    </div>
  );
};

export default CustomersList; 
